import api from '../api';
import { normalizeUrl } from '../../utils/url';
import { USE_LIVE_SEARCH_MOCKS, getFormPostMockResponse } from '../forms/formMockConfig';
import {
  mockLiveSearchCatalog,
  filterMockLiveSearchResults,
} from '../../mocks/mock-live-search-catalog';
import { getLiveSearchFillMockResponse } from '../../mocks/mock-livesearch-fill';
import { mockLiveSearchEmpty } from '../../mocks/mock-live-search';
import { hasLiveSearchQuery } from '../../domain/live-search/liveSearchUseCase';

/**
 * POST live search results
 * @param {string} searchUrl - URL from form searchUrl (e.g. /livesearch-results/{classid})
 * @param {ApiLiveSearchRequest} body
 * @returns {Promise<ApiLiveSearchResponse>}
 */
export const postLiveSearchResults = async (searchUrl, body) => {
  if (!searchUrl) {
    throw new Error('searchUrl is required');
  }

  if (!hasLiveSearchQuery(body)) {
    return mockLiveSearchEmpty;
  }

  if (USE_LIVE_SEARCH_MOCKS) {
    const filtered = filterMockLiveSearchResults(mockLiveSearchCatalog.results, body);

    // Brief delay so loading skeletons are visible in mock / Storybook
    await new Promise((resolve) => {
      setTimeout(resolve, 300);
    });

    return { results: filtered };
  }

  const url = normalizeUrl(searchUrl);
  const response = await api.post(url, body);
  return response.data;
};

/**
 * GET live search auto-submit action (replaces former POST with full form payload).
 * @param {string} autoSubmitUrl - Full URL from live search action autoSubmitUrl
 * @returns {Promise<ApiFormPostResponse>}
 */
export const getLiveSearchAutoSubmit = async (autoSubmitUrl) => {
  if (!autoSubmitUrl) {
    throw new Error('autoSubmitUrl is required');
  }

  if (USE_LIVE_SEARCH_MOCKS) {
    const mockResponse = await getFormPostMockResponse(autoSubmitUrl);
    if (mockResponse) {
      return mockResponse;
    }
  }

  const url = normalizeUrl(autoSubmitUrl);
  const response = await api.get(url);
  return response.data;
};

/**
 * GET livesearch-fill action (fields to apply + optional editSendFormUrl).
 * @param {string} fieldsUrl - Full URL from live search action fieldsUrl
 * @returns {Promise<ApiLiveSearchFillResponse>}
 */
export const getLiveSearchFill = async (fieldsUrl) => {
  if (!fieldsUrl) {
    throw new Error('fieldsUrl is required');
  }

  if (USE_LIVE_SEARCH_MOCKS) {
    const mockResponse = await getLiveSearchFillMockResponse(fieldsUrl);
    if (mockResponse) {
      return mockResponse;
    }
  }

  const url = normalizeUrl(fieldsUrl);
  const response = await api.get(url);
  return response.data;
};
